import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, X, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Product } from '@/types/product';

export interface FilterState {
  searchQuery: string;
  category: string;
  subcategory: string;
  badge: string;
  priceRange: [number, number];
  sortBy: string;
  inStock: boolean;
}

interface SearchAndFilterProps {
  products: Product[];
  onFilterChange: (filteredProducts: Product[], filters: FilterState) => void;
  className?: string;
}

const SearchAndFilter: React.FC<SearchAndFilterProps> = ({
  products,
  onFilterChange,
  className = ''
}) => {
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: '',
    category: 'all',
    subcategory: 'all',
    badge: 'all',
    priceRange: [0, 200],
    sortBy: 'featured',
    inStock: false
  });

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Extract unique values for filter options
  const filterOptions = useMemo(() => {
    const categories = [...new Set(products.map(p => p.category))];
    const subcategories = [...new Set(products.map(p => p.subcategory))];
    const badges = [...new Set(products.map(p => p.badge).filter(Boolean))];
    const prices = products.map(p => p.price);
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);

    return { categories, subcategories, badges, maxPrice, minPrice };
  }, [products]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(product =>
        product.name.toLowerCase().includes(query) ||
        product.description.toLowerCase().includes(query) ||
        product.features?.some(feature => feature.toLowerCase().includes(query)) ||
        product.category.toLowerCase().includes(query) ||
        product.subcategory?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (filters.category !== 'all') {
      result = result.filter(p => p.category === filters.category);
    }

    // Subcategory filter
    if (filters.subcategory !== 'all') {
      result = result.filter(p => p.subcategory === filters.subcategory);
    }

    // Badge filter
    if (filters.badge !== 'all') {
      result = result.filter(p => p.badge === filters.badge);
    }

    // Price range filter
    result = result.filter(p => 
      p.price >= filters.priceRange[0] && p.price <= filters.priceRange[1]
    );

    // In stock filter
    if (filters.inStock) {
      result = result.filter(p => 
        p.variants.some(v => v.available)
      );
    }

    // Sorting
    switch (filters.sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'rating':
        result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'newest':
        result.sort((a, b) => {
          const aIsNew = a.badge === 'NEW' ? 1 : 0;
          const bIsNew = b.badge === 'NEW' ? 1 : 0;
          return bIsNew - aIsNew;
        });
        break;
      case 'featured':
      default:
        result.sort((a, b) => {
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return (b.rating || 0) - (a.rating || 0);
        });
        break;
    }

    return result;
  }, [products, filters]);

  // Update parent component when filters change
  React.useEffect(() => {
    onFilterChange(filteredProducts, filters);
  }, [filteredProducts, filters, onFilterChange]);

  const updateFilter = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      searchQuery: '',
      category: 'all',
      subcategory: 'all',
      badge: 'all',
      priceRange: [filterOptions.minPrice, filterOptions.maxPrice],
      sortBy: 'featured',
      inStock: false
    });
    setShowAdvancedFilters(false);
  };

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'searchQuery') return value !== '';
    if (key === 'priceRange') return value[0] !== filterOptions.minPrice || value[1] !== filterOptions.maxPrice;
    if (key === 'sortBy') return value !== 'featured';
    if (key === 'inStock') return value === true;
    return value !== 'all';
  }).length;

  return (
    <div className={`bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-xl p-4 md:p-6 ${className}`}>
      {/* Main Search and Quick Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search Input */}
        <div className="relative flex-1">
          <div className={`relative transition-all duration-200 ${isSearchFocused ? 'scale-[1.02]' : ''}`}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search products, features, or categories..."
              value={filters.searchQuery}
              onChange={(e) => updateFilter('searchQuery', e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              className="w-full pl-10 pr-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-lime-400 focus:ring-1 focus:ring-lime-400 focus:outline-none transition-all duration-200"
            />
          </div>
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap lg:flex-nowrap gap-3">
          {/* Category Filter */}
          <Select value={filters.category} onValueChange={(value) => updateFilter('category', value)}>
            <SelectTrigger className="w-full lg:w-40 bg-white/10 border-gray-600 text-white">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-600 text-white">
              <SelectItem value="all" className="text-white hover:bg-gray-700">All Categories</SelectItem>
              {filterOptions.categories.map((category) => (
                <SelectItem key={category} value={category} className="text-white hover:bg-gray-700 capitalize">
                  {category.replace('s', "'s")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort By */}
          <Select value={filters.sortBy} onValueChange={(value) => updateFilter('sortBy', value)}>
            <SelectTrigger className="w-full lg:w-40 bg-white/10 border-gray-600 text-white">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-600 text-white">
              <SelectItem value="featured" className="text-white hover:bg-gray-700">Featured</SelectItem>
              <SelectItem value="price-low" className="text-white hover:bg-gray-700">Price: Low to High</SelectItem>
              <SelectItem value="price-high" className="text-white hover:bg-gray-700">Price: High to Low</SelectItem>
              <SelectItem value="name" className="text-white hover:bg-gray-700">Name A-Z</SelectItem>
              <SelectItem value="rating" className="text-white hover:bg-gray-700">Highest Rated</SelectItem>
              <SelectItem value="newest" className="text-white hover:bg-gray-700">Newest First</SelectItem>
            </SelectContent>
          </Select>

          {/* Advanced Filters Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="bg-white/10 border-gray-600 text-white hover:bg-white/20 relative"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-lime-400 text-black text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Advanced Filters */}
      <AnimatePresence>
        {showAdvancedFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-6 border-t border-gray-700 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Subcategory Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Subcategory</label>
                  <Select value={filters.subcategory} onValueChange={(value) => updateFilter('subcategory', value)}>
                    <SelectTrigger className="bg-white/10 border-gray-600 text-white">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600 text-white">
                      <SelectItem value="all" className="text-white hover:bg-gray-700">All Types</SelectItem>
                      {filterOptions.subcategories.map((sub) => (
                        <SelectItem key={sub} value={sub} className="text-white hover:bg-gray-700 capitalize">
                          {sub}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Badge Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Badge</label>
                  <Select value={filters.badge} onValueChange={(value) => updateFilter('badge', value)}>
                    <SelectTrigger className="bg-white/10 border-gray-600 text-white">
                      <SelectValue placeholder="All Badges" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600 text-white">
                      <SelectItem value="all" className="text-white hover:bg-gray-700">All Products</SelectItem>
                      {filterOptions.badges.map((badge) => (
                        <SelectItem key={badge} value={badge} className="text-white hover:bg-gray-700">
                          {badge}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Price Range</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.priceRange[0]}
                      onChange={(e) => updateFilter('priceRange', [parseInt(e.target.value) || 0, filters.priceRange[1]])}
                      className="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded text-white text-sm focus:border-lime-400 focus:ring-1 focus:ring-lime-400 focus:outline-none"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.priceRange[1]}
                      onChange={(e) => updateFilter('priceRange', [filters.priceRange[0], parseInt(e.target.value) || 200])}
                      className="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded text-white text-sm focus:border-lime-400 focus:ring-1 focus:ring-lime-400 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Stock Filter */}
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="inStock"
                    checked={filters.inStock}
                    onChange={(e) => updateFilter('inStock', e.target.checked)}
                    className="w-4 h-4 text-lime-400 bg-white/10 border-gray-600 rounded focus:ring-lime-400 focus:ring-2"
                  />
                  <label htmlFor="inStock" className="text-sm font-medium text-gray-300">
                    In Stock Only
                  </label>
                </div>
              </div>

              {/* Clear Filters */}
              {activeFilterCount > 0 && (
                <div className="mt-4 flex justify-between items-center">
                  <span className="text-sm text-gray-400">
                    {filteredProducts.length} products found
                  </span>
                  <Button
                    variant="ghost"
                    onClick={clearFilters}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear all filters
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results Summary */}
      {!showAdvancedFilters && (
        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-400">
            {filteredProducts.length} of {products.length} products
          </span>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-gray-400 hover:text-white text-sm"
            >
              <X className="w-3 h-3 mr-1" />
              Clear filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAndFilter; 